import {FormLabel} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import config from '../../../../app/config';
import _ from 'lodash';
import {ReferralTreatmentStatus, WaitingTimeMeasurementType} from '../../../../controls/agyle/dictionaries';
import {useStylesECDS} from './ecdsStyle';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import {useSelector} from 'react-redux';
import {referralTreatmentStatus, waitingTimeMeasurementType} from '../../../../store/reducers/dicts';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {cleanObject} from '../../../../util/patientContextUtil';

const Pathway = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    // Patient Pathway Dataset
    const [pathwayData, setPathwayData] = useState({
        UniqueBookingReferenceNumber_Converted: '',
        PatientPathwayIdentifier: '',
        OrganisationIdentifier_PatientPathwayIdentifierIssuer: '',
        ReferralToTreatmentPeriod_Status: null,
        WaitingTimeMeasurementType: null,
        ReferralToTreatmentPeriod_StartDate: null,
        ReferralToTreatmentPeriod_EndDate: null,
    });

    // Dictionaries
    const treatmentStatusDict = useSelector(referralTreatmentStatus);
    const waitingTimeMeasurementTypeDict = useSelector(waitingTimeMeasurementType);

    // For ReadOnly Data
    const disableData = useRef({});

    // invalid pickers
    const errorPicker = useRef({
        start: null,
        end: null,
    });

    // Validation state
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        bookingNr: false,
        PatientId: false,
        orgId: false,
        status: false,
        type: false,
    });

    // Initial Data
    useEffect(() => {
        let data = {};
        let dataToBlock = {};
        if (props.patient?.studies?.EmergencyCare?.PatientPathway) {
            data = {
                ...props.patient?.studies?.EmergencyCare?.PatientPathway?.PatientPathwayIdentity,
                ...props.patient?.studies?.EmergencyCare?.PatientPathway?.RTTPeriodCharacteristics,
            };
        }
        data = {
            ...data,
            ReferralToTreatmentPeriod_StartDate: data.ReferralToTreatmentPeriod_StartDate || null,
            ReferralToTreatmentPeriod_EndDate: data.ReferralToTreatmentPeriod_EndDate || null,
            ReferralToTreatmentPeriod_Status: data?.ReferralToTreatmentPeriod_Status ?
                _.find(treatmentStatusDict, {__code: data?.ReferralToTreatmentPeriod_Status}) : null,
            WaitingTimeMeasurementType: data?.WaitingTimeMeasurementType ?
                _.find(waitingTimeMeasurementTypeDict, {__code: data?.WaitingTimeMeasurementType}) : null,
        };

        setPathwayData(prevState => ({...prevState, ...data}));
        disableData.current = {...cleanObject(dataToBlock)};
    }, []);

    const updateData = () => {
        const data = {
            PatientPathwayIdentity: {
                UniqueBookingReferenceNumber_Converted: pathwayData.UniqueBookingReferenceNumber_Converted,
                PatientPathwayIdentifier: pathwayData.PatientPathwayIdentifier,
                OrganisationIdentifier_PatientPathwayIdentifierIssuer: pathwayData.OrganisationIdentifier_PatientPathwayIdentifierIssuer,
            },
            RTTPeriodCharacteristics: {
                ReferralToTreatmentPeriod_Status: pathwayData.ReferralToTreatmentPeriod_Status?.__code || '',
                WaitingTimeMeasurementType: pathwayData.WaitingTimeMeasurementType?.__code || '',
                ReferralToTreatmentPeriod_StartDate: pathwayData.ReferralToTreatmentPeriod_StartDate,
                ReferralToTreatmentPeriod_EndDate: pathwayData.ReferralToTreatmentPeriod_EndDate,
            },
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (validate()) {
                updateData();
            }
        }, 400);
        return () => clearTimeout(timeoutId);
    }, [pathwayData]);

    const changeHandler = (event) => {
        const {name, value} = event.target;
        setPathwayData((prevState) => ({...prevState, [name]: value}));
    };

    const validate = () => {
        let check = true;
        if (validationEnabled) {
            const touch = !!pathwayData.UniqueBookingReferenceNumber_Converted || !!pathwayData.PatientPathwayIdentifier ||
                !!pathwayData.OrganisationIdentifier_PatientPathwayIdentifierIssuer || !!pathwayData.ReferralToTreatmentPeriod_Status ||
                !!pathwayData.WaitingTimeMeasurementType || !!pathwayData.ReferralToTreatmentPeriod_StartDate ||
                !!pathwayData.ReferralToTreatmentPeriod_EndDate;

            let errorData = {
                bookingNr: touch ? (pathwayData.PatientPathwayIdentifier !== '' ? false : !props.checkLenght(12, 12, pathwayData.UniqueBookingReferenceNumber_Converted)) : false,
                patientId: touch ? (pathwayData.UniqueBookingReferenceNumber_Converted !== '' ? false : !props.checkLenght(20, 20, pathwayData.PatientPathwayIdentifier)) : false,
                orgId: touch ? !props.checkLenght(3, 5, pathwayData.OrganisationIdentifier_PatientPathwayIdentifierIssuer) : false,
                status: touch ? !props.checkLenght(2, 2, pathwayData.ReferralToTreatmentPeriod_Status?.__code) : false,
                type: touch ? !props.checkLenght(2, 2, pathwayData.WaitingTimeMeasurementType?.__code) : false,
            };
            setError(prevState => ({...prevState, ...errorData}));

            for (let prop in errorData) {
                if (errorData[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Patient Pathway',
                    groupStatus: 'O',
                    touch: touch,
                    mandatory: (pathwayData.PatientPathwayIdentifier === '' ? !pathwayData.UniqueBookingReferenceNumber_Converted : false) ||
                        (pathwayData.UniqueBookingReferenceNumber_Converted === '' ? !pathwayData.PatientPathwayIdentifier : false) ||
                        !pathwayData.OrganisationIdentifier_PatientPathwayIdentifierIssuer || !pathwayData.ReferralToTreatmentPeriod_Status ||
                        !pathwayData.WaitingTimeMeasurementType,
                    errors: !check,
                    pickers: !!errorPicker.current.start || !!errorPicker.current.end,
                };
                props.valid(prevState => ({...prevState, 'pathway': validObj}));
            }
        }
        return check;
    };

    const dateHandler = (name, event) => {
        if (event) {
            const date = moment(event).format(config.ecdsDateFormat);
            setPathwayData((prevState) => ({...prevState, [name]: date}));
        }
    };

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    if (props.accordion !== 'Patient Pathway') return null;

    return (
        <div className={classes.root}>
            <div style={{marginBottom: 10}}>
                <FormLabel className={classes.label}>
                    {t('Patient Pathway Identity')}
                </FormLabel>
            </div>

            <Grid container spacing={2} direction="row">
                <Grid item xs={12} md={4}>
                    <AgyleTextField
                        label={t('Unique Booking Reference Number')}
                        name="UniqueBookingReferenceNumber_Converted"
                        onChange={changeHandler}
                        fullWidth
                        type="number"
                        value={pathwayData.UniqueBookingReferenceNumber_Converted}
                        error={error.bookingNr}
                        helperText={error.bookingNr ? 'Input must be 12 characters long.' : ''}
                        disabled={!!pathwayData.PatientPathwayIdentifier}
                        readOnly={!!disableData?.current.UniqueBookingReferenceNumber_Converted}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <AgyleTextField
                        label={t('Patient Pathway Identifier')}
                        name="PatientPathwayIdentifier"
                        onChange={changeHandler}
                        fullWidth
                        value={pathwayData.PatientPathwayIdentifier}
                        error={error.patientId}
                        helperText={error.patientId ? 'Input must be 20 characters long.' : ''}
                        disabled={!!pathwayData.UniqueBookingReferenceNumber_Converted}
                        readOnly={!!disableData?.current.PatientPathwayIdentifier}
                        maxLength={20}
                        minLength={20}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <AgyleTextField
                        label={t('Patient Pathway Identifier Issuer')}
                        name="OrganisationIdentifier_PatientPathwayIdentifierIssuer"
                        onChange={changeHandler}
                        fullWidth
                        value={pathwayData.OrganisationIdentifier_PatientPathwayIdentifierIssuer}
                        error={error.orgId}
                        helperText={error.orgId ? 'Input must be between 3 and 5 characters long.' : ''}
                        readOnly={!!disableData?.current.OrganisationIdentifier_PatientPathwayIdentifierIssuer}
                        maxLength={5}
                        minLength={3}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>

            <div style={{marginTop: 5, marginBottom: 10}}>
                <FormLabel className={classes.label}>
                    {t('Treatment Period Characteristics')}
                </FormLabel>
            </div>

            <Grid container spacing={2} direction="row">
                <Grid item xs={12} md={6}>
                    <ReferralTreatmentStatus
                        name="ReferralToTreatmentPeriod_Status"
                        label={t('Treatment Period Status')}
                        value={pathwayData.ReferralToTreatmentPeriod_Status}
                        onChange={(_e, val) => setDataDictionary(['ReferralToTreatmentPeriod_Status', val])}
                        disabled={!!disableData?.current.ReferralToTreatmentPeriod_Status}
                        error={error.status}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <WaitingTimeMeasurementType
                        name="WaitingTimeMeasurementType"
                        label={t('Waiting Time Measurement Type')}
                        color="primary"
                        value={pathwayData.WaitingTimeMeasurementType}
                        onChange={(_e, val) => setDataDictionary(['WaitingTimeMeasurementType', val])}
                        disabled={!!disableData?.current.WaitingTimeMeasurementType}
                        error={error.type}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <AgyleDatePicker
                        name="ReferralToTreatmentPeriod_StartDate"
                        label={t('Treatment Period Start Date')}
                        onChange={(e) => dateHandler('ReferralToTreatmentPeriod_StartDate', e)}
                        value={pathwayData.ReferralToTreatmentPeriod_StartDate}
                        type="date"
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            start: event,
                        }}
                        readOnly={!!disableData?.current.ReferralToTreatmentPeriod_StartDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <AgyleDatePicker
                        name="ReferralToTreatmentPeriod_EndDate"
                        label={t('Treatment Period End Date')}
                        onChange={(e) => dateHandler('ReferralToTreatmentPeriod_EndDate', e)}
                        value={pathwayData.ReferralToTreatmentPeriod_EndDate}
                        type="date"
                        minDate={moment()}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            end: event,
                        }}
                        readOnly={!!disableData?.current.ReferralToTreatmentPeriod_EndDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default Pathway;
